import { useEffect, useState } from "react";
import { PhoneCall, Send, BarChart3, ArrowRight, MessageCircle, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full cursor-not-allowed rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-slate-500 placeholder:text-slate-600 outline-none";
const secretInputCls = "w-full rounded-xl border border-emerald-400/20 bg-black/20 px-4 py-3 text-sm text-slate-100 outline-none transition-colors placeholder:text-slate-600 focus:border-emerald-400/55";
const Badge = () => <span className="rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#F3E5AB]">Макет · Скоро</span>;
const messageCentralCustomerDraftKey = "messagecentral_customer_id_draft";
const integrationSecretProps = {
  type: "text",
  autoComplete: "new-password",
  "data-lpignore": "true",
  "data-1p-ignore": "true",
  style: { WebkitTextSecurity: "disc" },
};

const MessageCentralOtp = ({ api }) => {
  const [customerId, setCustomerId] = useState(() => sessionStorage.getItem(messageCentralCustomerDraftKey) || "");
  const [authToken, setAuthToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [configured, setConfigured] = useState(null);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let active = true;
    api.get("/admin/integrations/messagecentral-otp")
      .then((response) => {
        if (!active) return;
        setConfigured(Boolean(response.data?.configured));
        setEnabled(Boolean(response.data?.enabled));
      })
      .catch(() => { if (active) setConfigured(false); });
    return () => { active = false; };
  }, [api]);

  const connect = async (event) => {
    event.preventDefault();
    if (!/^C-[A-Za-z0-9_-]{6,}$/.test(customerId.trim()) || authToken.trim().length < 16) {
      toast.error("Проверьте реквизиты Message Central", { description: "Вставьте Customer ID и Auth Token из API Credentials." });
      return;
    }
    setBusy(true);
    try {
      const response = await api.post("/admin/integrations/messagecentral-otp", { customer_id: customerId.trim(), auth_token: authToken.trim() });
      setCustomerId("");
      setAuthToken("");
      sessionStorage.removeItem(messageCentralCustomerDraftKey);
      setConfigured(true);
      setEnabled(response.data?.enabled !== false);
      toast.success("WhatsApp OTP подключён", { description: "Ключи сохранены только на сервере. Публичные формы требуют подтверждения номера." });
    } catch (error) {
      toast.error("Не удалось подключить WhatsApp OTP", { description: error?.response?.data?.detail || "Проверьте реквизиты Message Central и повторите попытку." });
    } finally {
      setBusy(false);
    }
  };

  const toggle = async () => {
    if (!configured) return;
    setBusy(true);
    try {
      const response = await api.patch("/admin/integrations/messagecentral-otp", { enabled: !enabled });
      setEnabled(Boolean(response.data?.enabled));
      toast.success(response.data?.enabled ? "WhatsApp OTP включён" : "WhatsApp OTP отключён", {
        description: response.data?.enabled
          ? "Подтверждение номера снова обязательно до создания заявки."
          : "Публичные формы теперь отправляют заявку без кода WhatsApp, но с обязательным согласием.",
      });
    } catch (error) {
      toast.error("Не удалось изменить режим WhatsApp OTP", { description: error?.response?.data?.detail || "Повторите попытку." });
    } finally {
      setBusy(false);
    }
  };

  const status = configured === null ? "Проверка" : configured && enabled ? "Подключено" : configured ? "Отключено" : "Настройка";
  const statusCls = configured && enabled
    ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300"
    : "border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#F3E5AB]";

  return (
    <div data-testid="integration-messagecentral-otp" className="rounded-[1.5rem] border border-emerald-400/25 bg-gradient-to-b from-emerald-400/[0.07] to-[#12141C] p-5 sm:rounded-[2rem] sm:p-7">
      <div className="flex items-start justify-between gap-4"><span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-emerald-400/25 bg-emerald-400/8"><MessageCircle className="h-6 w-6 text-emerald-300" /></span><span data-testid="messagecentral-status" className={`rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] ${statusCls}`}>{status}</span></div>
      <h3 className="mt-5 text-lg font-semibold text-slate-100">WhatsApp OTP · Message Central</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-500">Номер заявителя подтверждается одноразовым кодом WhatsApp до создания заявки. Ключи хранятся только на сервере; публичные формы не получают реквизиты интеграции.</p>
      {configured === true && (
        <div className="mt-5 rounded-2xl border border-white/10 bg-black/15 p-4" data-testid="messagecentral-otp-toggle">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-slate-200">Обязательное подтверждение WhatsApp</p>
              <p className="mt-1 text-xs leading-relaxed text-slate-500">
                {enabled
                  ? "Перед сохранением заявки посетитель должен подтвердить номер кодом WhatsApp."
                  : "Проверка временно выключена: заявка сохраняется после согласия, без запроса к Message Central."}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              onClick={toggle}
              disabled={busy}
              data-testid="messagecentral-otp-enabled-toggle"
              className={`relative h-8 w-14 shrink-0 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${enabled ? "bg-emerald-500" : "bg-slate-700"}`}
            >
              <span className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-7" : "translate-x-1"}`} />
            </button>
          </div>
          <button type="button" onClick={toggle} disabled={busy} className="mt-4 text-xs font-semibold text-[#F3E5AB] underline-offset-4 hover:underline disabled:opacity-60">
            {enabled ? "Отключить проверку WhatsApp" : "Включить проверку WhatsApp"}
          </button>
        </div>
      )}
      {configured === false && <form onSubmit={connect} className="mt-5 space-y-3">
        <input {...integrationSecretProps} name="messagecentral_customer_id" spellCheck="false" value={customerId} onChange={(event) => { setCustomerId(event.target.value); sessionStorage.setItem(messageCentralCustomerDraftKey, event.target.value); }} placeholder="Customer ID" data-testid="messagecentral-customer-input" className={secretInputCls} />
        <input {...integrationSecretProps} name="messagecentral_auth_token" spellCheck="false" value={authToken} onChange={(event) => setAuthToken(event.target.value)} placeholder="Auth Token" data-testid="messagecentral-token-input" className={secretInputCls} />
        <button type="submit" disabled={busy} data-testid="messagecentral-connect" className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#D4AF37] px-4 py-3 text-sm font-semibold text-[#0A0B0E] transition-all hover:shadow-[0_0_30px_rgba(212,175,55,0.18)] disabled:cursor-not-allowed disabled:opacity-55">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Подключить WhatsApp OTP</button>
      </form>}
    </div>
  );
};

const OtpCom = ({ api }) => {
  const [configured, setConfigured] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    api.get("/admin/integrations/otp-com")
      .then((response) => {
        if (!active) return;
        setConfigured(Boolean(response.data?.configured));
        setEnabled(Boolean(response.data?.enabled));
      })
      .catch(() => { if (active) setConfigured(false); });
    return () => { active = false; };
  }, [api]);

  const toggle = async () => {
    if (!configured) return;
    setBusy(true);
    try {
      const response = await api.patch("/admin/integrations/otp-com", { enabled: !enabled });
      setEnabled(Boolean(response.data?.enabled));
      toast.success(response.data?.enabled ? "OTP.com включён" : "OTP.com отключён", {
        description: response.data?.enabled
          ? "WhatsApp — основной канал; SMS используется только как резервный маршрут."
          : "Публичные формы временно отправляют заявку без кода подтверждения.",
      });
    } catch (error) {
      toast.error("Не удалось изменить режим OTP.com", { description: error?.response?.data?.detail || "Повторите попытку." });
    } finally {
      setBusy(false);
    }
  };

  const status = configured === null ? "Проверка" : configured && enabled ? "Подключено" : configured ? "Отключено" : "Настройка";
  const statusCls = configured && enabled
    ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300"
    : "border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#F3E5AB]";

  return (
    <div data-testid="integration-otp-com" className="rounded-[1.5rem] border border-emerald-400/25 bg-gradient-to-b from-emerald-400/[0.07] to-[#12141C] p-5 sm:rounded-[2rem] sm:p-7">
      <div className="flex items-start justify-between gap-4"><span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-emerald-400/25 bg-emerald-400/8"><MessageCircle className="h-6 w-6 text-emerald-300" /></span><span data-testid="otp-com-status" className={`rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] ${statusCls}`}>{status}</span></div>
      <h3 className="mt-5 text-lg font-semibold text-slate-100">OTP.com · WhatsApp / SMS</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-500">Проверка номера до создания заявки. Код отправляется сначала в WhatsApp; если код не введён в течение минуты, однократно отправляется SMS на тот же номер. Telegram и email не используются. API-ключ и подпись webhook хранятся только на сервере.</p>
      {configured && (
        <div className="mt-5 rounded-2xl border border-white/10 bg-black/15 p-4" data-testid="otp-com-toggle">
          <div className="flex items-center justify-between gap-4">
            <div><p className="text-sm font-medium text-slate-200">Обязательное подтверждение номера</p><p className="mt-1 text-xs leading-relaxed text-slate-500">{enabled ? "Перед сохранением заявки посетитель подтверждает номер через OTP.com." : "Проверка временно выключена: заявка сохраняется только после согласия."}</p></div>
            <button type="button" role="switch" aria-checked={enabled} onClick={toggle} disabled={busy} data-testid="otp-com-enabled-toggle" className={`relative h-8 w-14 shrink-0 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${enabled ? "bg-emerald-500" : "bg-slate-700"}`}><span className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-7" : "translate-x-1"}`} /></button>
          </div>
          <button type="button" onClick={toggle} disabled={busy} className="mt-4 text-xs font-semibold text-[#F3E5AB] underline-offset-4 hover:underline disabled:opacity-60">{enabled ? "Отключить OTP.com" : "Включить OTP.com"}</button>
        </div>
      )}
      {!configured && <p className="mt-5 rounded-xl border border-[#D4AF37]/20 bg-[#D4AF37]/5 p-3 text-xs leading-relaxed text-[#F3E5AB]">Ожидается серверная настройка ключа и подписи webhook.</p>}
    </div>
  );
};

export const Integrations = ({ setTab, api }) => (
  <div data-testid="admin-integrations" className="max-w-4xl space-y-6">
    <div><h1 className="font-serif text-2xl text-slate-50 sm:text-3xl">Интеграции</h1><p className="mt-2 max-w-xl text-sm text-slate-500">Подключённые сервисы и будущие расширения проекта.</p></div>
    <div className="grid gap-5 lg:grid-cols-2">
      <OtpCom api={api} />
      <MessageCentralOtp api={api} />
      <div data-testid="integration-metrika" className="rounded-[1.5rem] border border-emerald-400/25 bg-gradient-to-b from-emerald-400/[0.06] to-[#12141C] p-5 sm:rounded-[2rem] sm:p-7"><div className="flex items-start justify-between gap-4"><span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-emerald-400/25 bg-emerald-400/8"><BarChart3 className="h-6 w-6 text-emerald-300" /></span><span className="rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-emerald-300">Подключено</span></div><h3 className="mt-5 text-lg font-semibold text-slate-100">Яндекс.Метрика</h3><p className="mt-2 text-sm leading-relaxed text-slate-500">Нативный счётчик и Reporting API поставляют в dashboard только реальные визиты, страны, источники и подробную статистику. Тестовые pageview отключены.</p><button onClick={() => setTab("settings")} data-testid="integration-metrika-open-settings" className="mt-5 inline-flex items-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-400/10 px-5 py-3 text-xs font-semibold text-emerald-100 transition-colors hover:bg-emerald-400/15">Открыть настройки Метрики <ArrowRight className="h-4 w-4" /></button></div>
      <div data-testid="integration-telephony" className="rounded-[1.5rem] border border-white/8 bg-[#12141C] p-5 sm:rounded-[2rem] sm:p-7"><div className="flex items-start justify-between gap-4"><span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-sky-400/25 bg-sky-400/8"><PhoneCall className="h-6 w-6 text-sky-400" /></span><Badge /></div><h3 className="mt-5 text-lg font-semibold text-slate-100">API-телефония</h3><p className="mt-2 text-sm leading-relaxed text-slate-500">Автодозвон по новым заявкам: звонок клиенту в течение минуты после отправки формы.</p><input disabled placeholder="API-ключ телефонии" className={`${inputCls} mt-5`} /></div>
      <div data-testid="integration-telegram" className="rounded-[1.5rem] border border-white/8 bg-[#12141C] p-5 sm:rounded-[2rem] sm:p-7"><div className="flex items-start justify-between gap-4"><span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-sky-400/25 bg-sky-400/8"><Send className="h-6 w-6 text-sky-400" /></span><Badge /></div><h3 className="mt-5 text-lg font-semibold text-slate-100">Telegram-бот</h3><p className="mt-2 text-sm leading-relaxed text-slate-500">Каждая новая заявка мгновенно приходит в ваш Telegram-чат с кнопками смены статуса.</p><div className="mt-5 grid gap-3 sm:grid-cols-2"><input disabled placeholder="Токен бота" className={inputCls} /><input disabled placeholder="ID чата" className={inputCls} /></div></div>
    </div>
  </div>
);
